/* BirthdayWidget — toont aankomende verjaardagen in de Groepsapp voor de kinderen
 * die vandaag in de dag-planning staan. Toont de volgende 30 dagen, inclusief vandaag
 * (extra prominent met een 🎉-badge). */
import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Cake } from "lucide-react";

/** Bereken hoeveel dagen tot de eerstvolgende verjaardag. */
function daysUntilBirthday(iso, today) {
  if (!iso) return null;
  try {
    const gb = new Date(iso);
    const next = new Date(today.getFullYear(), gb.getMonth(), gb.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    const days = Math.round((next - today) / 86400000);
    return { days, date: next, ageThen: next.getFullYear() - gb.getFullYear() };
  } catch {
    return null;
  }
}

export default function BirthdayWidget({ items }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const birthdays = useMemo(() => {
    return (items || [])
      .map(it => {
        const meta = daysUntilBirthday(it.kind?.geboortedatum, today);
        if (!meta) return null;
        return { ...meta, kind: it.kind };
      })
      .filter(x => x && x.days <= 30)
      .sort((a, b) => a.days - b.days);
  }, [items, today]);

  if (birthdays.length === 0) return null;

  return (
    <Card className="p-3 bg-white shadow-none" data-testid="birthday-widget">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center bg-pink-100 text-pink-600">
          <Cake className="h-4 w-4" />
        </div>
        <div className="text-sm font-medium text-neutral-800">Aankomende verjaardagen</div>
        <div className="text-[10px] text-neutral-500 ml-auto">Komende 30 dagen</div>
      </div>
      <div className="space-y-1.5">
        {birthdays.map(b => {
          const isToday = b.days === 0;
          const isSoon = b.days > 0 && b.days <= 7;
          return (
            <div
              key={b.kind.id}
              className={`flex items-center gap-2 p-2 rounded ${isToday ? "bg-pink-50 border border-pink-200" : "bg-neutral-50"}`}
              data-testid={`birthday-item-${b.kind.id}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${isToday ? "bg-pink-500 text-white" : "bg-[color:var(--sage-100)] text-[color:var(--sage)]"}`}>
                {b.kind.voornaam?.[0]}{b.kind.achternaam?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-800 truncate">
                  {isToday && <span className="mr-1">🎉</span>}
                  {b.kind.voornaam} {b.kind.achternaam}
                </div>
                <div className="text-[11px] text-neutral-500">
                  {isToday
                    ? `Vandaag ${b.ageThen} jaar!`
                    : `${b.date.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })} · wordt ${b.ageThen} jaar`}
                </div>
              </div>
              {!isToday && (
                <div className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${isSoon ? "bg-amber-100 text-amber-700" : "bg-neutral-200 text-neutral-600"}`}>
                  {b.days === 1 ? "morgen" : `${b.days}d`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
